import React, { useCallback, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { FiVolume2 } from "react-icons/fi";
import { useSpeech } from "react-text-to-speech";

// Material UI
import Button from '@mui/material/Button';

// Styles
import styles from "./useReadAloudButton.module.scss";

// Types
interface ReadAloudOptions {
  className?: string;
  volume?: number;
  rate?: number;
  pitch?: number;
  voiceURI?: string;
  highlightMode?: 'word' | 'sentence';
  highlightProps?: {
    className?: string;
  };
}

interface UseReadAloudButtonReturn {
  ReadAloudButton: React.ComponentType<{ className?: string }>;
  Text: React.ComponentType<any>;
  speechStatus: string;
  start: () => void;
  stop: () => void;
}

const useReadAloudButton = (
  text: string,
  options: ReadAloudOptions = {}
): UseReadAloudButtonReturn => {
  const { t } = useTranslation();

  const {
    className = '',
    volume = 1,
    rate = 1,
    pitch = 1,
    voiceURI = 'urn:moz-tts:osx:com.apple.voice.compact.en-GB.Daniel',
    highlightMode = 'word',
    highlightProps = {}
  } = options;

  const {
    Text,         // Component that renders and highlights the spoken text
    speechStatus, // "started", "paused", etc.
    start,        // Function to start speech
    stop,         // Function to stop
  } = useSpeech({
    text: text || '',
    highlightText: true,
    highlightMode,
    highlightProps: {
      className: styles.highlightedText,
      ...highlightProps
    },
    volume,
    rate,
    pitch,
    voiceURI,
  });

  const handleReadAloud = useCallback(() => {
    if (speechStatus === 'started') {
      stop();
    } else {
      start();
    }
  }, [start, speechStatus, stop, text]);

  const ReadAloudButton = useMemo(() => {
    const ButtonComponent = ({ className: buttonClassName = styles.readAloudButton }: { className?: string }) => (
      <Button
        variant='outlined'
        color='primary'
        className={`${buttonClassName} ${className}`.trim()}
        onClick={handleReadAloud}
        startIcon={
          <FiVolume2 className={`text-xl ${speechStatus === 'started'
            ? 'animate-pulse text-purple-500'
            : 'text-azure-600'
            }`}
          />
        }
      >
        <span className='hidden md:block'>
          {t('actions.read_aloud')}
        </span>
      </Button>
    );

    ButtonComponent.displayName = 'ReadAloudButton';
    return ButtonComponent;
  }, [className, handleReadAloud, speechStatus, t]);

  return {
    ReadAloudButton,
    Text,
    speechStatus,
    start,
    stop
  };
};

export default useReadAloudButton;
